/**
 * The entry grid's side of the PlanView-measure formula host (Nexo-Formula-Language.md §9.2): DERIVED measures
 * are evaluated with @nexo/formula's interpreter over the AST the server stores (references by measure id), in
 * dependency order, so the grid shows what the server will store. The formula text is the fallback for a view
 * the backfill has not reached; a formula with nothing to evaluate is blank (§9.6) and reported in `unbound`, a
 * formula that fails only its value types keeps evaluating in Phase 1 and is reported in `warnings`, nothing is
 * thrown. INTERNAL_REVENUE_LINE measures are server-sourced and never evaluated here. The Total column totals a row
 * through @nexo/formula's CellAggregation (§9.3), the rule the server's rollups total by.
 */
import {
  AggregationMember,
  CellAggregation,
  FormulaNode,
  FormulaProfile,
  FormulaSymbol,
  FormulaTarget,
  FormulaError,
  FormulaValue,
  SymbolTable,
  Types,
  ValueType,
  canEvaluate,
  check,
  checkAst,
  children,
  evaluate,
  fromJson,
  isDecimal,
  pathText,
} from '@nexo/formula';
import type { EntryRow, MeasureLike, PeriodLike } from './planEntryRows';
import { atScale, storedScale } from './storedScale';

export interface PreparedFormula {
  measure: MeasureLike;
  root: FormulaNode;
  /** The measure codes the formula references. */
  operands: string[];
}

export interface PreparedFormulas {
  /** The evaluable DERIVED measures, in dependency order. */
  ordered: PreparedFormula[];
  /** DERIVED measures whose formula has nothing to evaluate against this view; their cells stay blank. */
  unbound: MeasureLike[];
  /** Evaluable measures whose value types fail (a price typed RATIO under an AMOUNT): tolerated in Phase 1, blank from Phase 2 (§9.6). */
  warnings: { measure: MeasureLike; error: FormulaError }[];
  codeById: ReadonlyMap<string, string>;
}

const VALUE_TYPES: Record<string, ValueType> = { QUANTITY: 'Quantity', AMOUNT: 'Amount', RATE: 'Rate', RATIO: 'Ratio' };

/** A view's measures as formula symbols: codes match case-insensitively; the id is the server's, or the code for an unsaved measure. */
class MeasureSymbols implements SymbolTable {
  private readonly byCode = new Map<string, FormulaSymbol>();
  private readonly byId = new Map<string, FormulaSymbol>();

  constructor(measures: MeasureLike[]) {
    for (const m of measures) {
      const symbol: FormulaSymbol = {
        id: m.id ?? m.code,
        name: m.code,
        type: Types.Decimal,
        valueType: VALUE_TYPES[m.valueType ?? ''] ?? 'Plain',
        nullable: true,
      };
      this.byCode.set(m.code.toUpperCase(), symbol);
      this.byId.set(symbol.id, symbol);
    }
  }

  resolve(path: readonly string[]): FormulaSymbol | undefined {
    return path.length === 1 ? this.byCode.get(path[0].toUpperCase()) : undefined;
  }

  resolveId(id: string): FormulaSymbol | undefined {
    return this.byId.get(id);
  }

  codeOf(id: string): string | undefined {
    return this.byId.get(id)?.name;
  }

  get codeById(): ReadonlyMap<string, string> {
    return new Map([...this.byId].map(([id, s]) => [id, s.name]));
  }
}

function targetOf(m: MeasureLike): FormulaTarget {
  return { type: Types.Decimal, valueType: VALUE_TYPES[m.valueType ?? ''] ?? 'Plain', nullable: true, rounding: 'halfEven' };
}

function hasStoredAst(m: MeasureLike): boolean {
  return m.formulaAst !== undefined && m.formulaAst !== null && m.formulaAst !== '';
}

/** The stored AST (the server sends the JSON as a string; tolerate an object), or null when there is none or it does not read. */
function storedRoot(m: MeasureLike): FormulaNode | null {
  if (!hasStoredAst(m)) return null;
  try {
    return fromJson(typeof m.formulaAst === 'string' ? m.formulaAst : JSON.stringify(m.formulaAst));
  } catch {
    return null;
  }
}

function referencedCodes(root: FormulaNode, symbols: MeasureSymbols): string[] {
  const codes: string[] = [];
  const walk = (node: FormulaNode): void => {
    if (node.kind === 'ref') {
      const code = (node.symbol !== null ? symbols.codeOf(node.symbol) : undefined) ?? pathText(node);
      if (!codes.some((c) => c.toUpperCase() === code.toUpperCase())) codes.push(code);
      return;
    }
    children(node).forEach(walk);
  };
  walk(root);
  return codes;
}

function isEvaluable(m: MeasureLike): boolean {
  return m.kind === 'DERIVED' && m.storageBinding !== 'INTERNAL_REVENUE_LINE' && Boolean(m.formulaAst || (m.formula && m.formula.trim()));
}

/**
 * Checks one measure's formula against the view: the resolved root when it has a value (§9.6), and its first error if
 * any. The text is the fallback only for a measure with no stored AST: one whose AST does not read (an AST version
 * from a later build, say) has nothing to evaluate, as on the server (MeasureFormulaEvaluator).
 */
function bind(m: MeasureLike, symbols: MeasureSymbols): { root: FormulaNode | null; error?: FormulaError } {
  if (!hasStoredAst(m)) {
    return bound(check(m.formula!, symbols, FormulaProfile.measure, targetOf(m)));
  }
  const stored = storedRoot(m);
  return stored ? bound(checkAst(stored, symbols, FormulaProfile.measure, targetOf(m))) : { root: null };
}

function bound(result: ReturnType<typeof check>): { root: FormulaNode | null; error?: FormulaError } {
  return { root: canEvaluate(result) ? result.root : null, error: result.errors[0] };
}

/**
 * Dependency order among the derived measures (declaration order among independents), and the ones no order
 * exists for: a cycle — which only data saved before the save guard refused cycles (SYS-4021) can hold — and every
 * measure that reads one. The server's evaluator leaves those blank (Unbound), so the grid does too.
 */
function order(prepared: PreparedFormula[]): { ordered: PreparedFormula[]; cyclic: PreparedFormula[] } {
  const codes = new Set(prepared.map((p) => p.measure.code.toUpperCase()));
  const placed = new Set<string>();
  const remaining = [...prepared];
  const ordered: PreparedFormula[] = [];
  for (;;) {
    const index = remaining.findIndex((p) =>
      p.operands.every((code) => !codes.has(code.toUpperCase()) || placed.has(code.toUpperCase())),
    );
    if (index < 0) return { ordered, cyclic: remaining };
    const next = remaining.splice(index, 1)[0];
    placed.add(next.measure.code.toUpperCase());
    ordered.push(next);
  }
}

/** Prepares a view's DERIVED formulas once; evaluate them per row with evaluateDerivedMeasures. */
export function prepareMeasureFormulas(measures: MeasureLike[]): PreparedFormulas {
  const symbols = new MeasureSymbols(measures);
  const prepared: PreparedFormula[] = [];
  const unbound: MeasureLike[] = [];
  const warnings: PreparedFormulas['warnings'] = [];
  for (const measure of measures.filter(isEvaluable)) {
    const { root, error } = bind(measure, symbols);
    if (!root) {
      unbound.push(measure);
      continue;
    }
    prepared.push({ measure, root, operands: referencedCodes(root, symbols) });
    if (error) warnings.push({ measure, error });
  }
  const { ordered, cyclic } = order(prepared);
  return { ordered, unbound: [...unbound, ...cyclic.map((p) => p.measure)], warnings, codeById: symbols.codeById };
}

function cellInput(row: EntryRow, periodId: string, code: string): number | null {
  const raw = row[`${periodId}__${code}`];
  if (raw === undefined || raw === null || raw === '') return null;
  const n = Number(raw);
  return Number.isNaN(n) ? null : n;
}

function toCell(value: FormulaValue): number | '' {
  return isDecimal(value) ? value.toNumber() : '';
}

/**
 * One derived value, or null when it lies outside what a number holds (§4.4: an error, never a value — the
 * formula runtime throws a RangeError). The server refuses that cell's save naming the measure, so the grid shows
 * the cell blank instead of a figure that will never be stored.
 */
function evaluateCell(root: FormulaNode, input: (id: string) => number | null): FormulaValue {
  try {
    return evaluate(root, input, { rounding: 'halfEven' });
  } catch (e) {
    if (e instanceof RangeError) return null;
    throw e;
  }
}

/** Writes every derived measure's value for every period into `row` (in place, as the row helpers do) and returns it. */
export function evaluateDerivedMeasures(row: EntryRow, prepared: PreparedFormulas, periods: PeriodLike[]): EntryRow {
  for (const p of periods) {
    // Blank first: an unbound measure has no value on the server, so a formula that reads it reads null — not the
    // amount the row was loaded with.
    for (const measure of prepared.unbound) row[`${p.id}__${measure.code}`] = '';
    for (const { measure, root } of prepared.ordered) {
      const value = evaluateCell(root, (id) => cellInput(row, p.id, prepared.codeById.get(id) ?? id));
      const cell = toCell(value);
      // A derived amount is stored at its column's scale (OQ 4); the cell shows what the save will store.
      row[`${p.id}__${measure.code}`] = cell === '' ? cell : atScale(cell, storedScale(measure));
    }
  }
  return row;
}

/**
 * The members of a view's cell aggregation (§9.3): every measure by its id, a derived one with the formula the
 * cells evaluate — the formulas an entered rate is weighted through, too — and an entered rate or ratio with the
 * measure its `weightedBy` names, when it names one. A derived measure whose formula does not bind totals like an
 * entered one without a weight: blank.
 */
function aggregationMembers(measures: MeasureLike[], prepared: PreparedFormulas): AggregationMember[] {
  const idOf = (m: MeasureLike) => m.id ?? m.code;
  const rootByCode = new Map(prepared.ordered.map((p) => [p.measure.code, p.root]));
  return measures.map((m) => {
    const named = m.weightedBy?.trim();
    const weight = named ? measures.find((w) => w.code.toUpperCase() === named.toUpperCase()) : undefined;
    return {
      id: idOf(m),
      valueType: VALUE_TYPES[m.valueType ?? ''] ?? 'Plain',
      formula: rootByCode.get(m.code) ?? null,
      weightedBy: named ? (weight ? idOf(weight) : named) : null,
    };
  });
}

/**
 * One measure's total over a group of rows (§9.3): every row's cell in every one of `periods` aggregated together — a
 * quantity per the unit of its row, so a group whose rows are in different units has no quantity total and weights no
 * rate by one. What the entry grid's row groups total a rate or ratio by. '' where the measure has no total.
 */
export function measureTotalOver(rows: EntryRow[], measures: MeasureLike[], periods: PeriodLike[], code: string): number | '' {
  const prepared = prepareMeasureFormulas(measures);
  const aggregation = new CellAggregation(aggregationMembers(measures, prepared));
  for (const r of rows) {
    for (const p of periods) {
      aggregation.add({
        value: (id) => cellInput(r, p.id, prepared.codeById.get(id) ?? id),
        unit: () => r.unitOfMeasureId ?? null,
      });
    }
  }
  const measure = measures.find((m) => m.code === code);
  return measure ? toCell(aggregation.total(measure.id ?? measure.code).value) : '';
}

/**
 * A row's totals over `periods` by measure code (§9.3): quantities and amounts sum, a derived rate or ratio is its
 * formula over the totals, an entered one is weighted by its `weightedBy` or else the factor its product formula
 * pairs it with. '' where a measure has no total.
 */
export function measureTotals(
  row: EntryRow,
  measures: MeasureLike[],
  periods: PeriodLike[],
  prepared: PreparedFormulas = prepareMeasureFormulas(measures),
): Map<string, number | ''> {
  const aggregation = new CellAggregation(aggregationMembers(measures, prepared));
  for (const p of periods) aggregation.add({ value: (id) => cellInput(row, p.id, prepared.codeById.get(id) ?? id) });
  return new Map(measures.map((m) => [m.code, toCell(aggregation.total(m.id ?? m.code).value)]));
}
